import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Filter, RotateCcw, ArrowRight, History, Layers, GalleryVertical } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
    Pagination, PaginationContent, PaginationItem, PaginationLink,
    PaginationNext, PaginationPrevious, PaginationEllipsis,
} from "@/components/ui/pagination";
import { SessionCard } from "@/components/SessionCard";
import { AvatarPortrait } from "@/components/AvatarPortrait";
import { EmptyState } from "@/components/EmptyState";
import { AvatarCardSkeleton } from "@/components/Skeleton";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 12;

const SORT_OPTIONS = [
    { value: "recent", label: "Most recent" },
    { value: "oldest", label: "Oldest first" },
    { value: "score_desc", label: "Highest score" },
    { value: "score_asc", label: "Lowest score" },
];

const STATUS_OPTIONS = [
    { value: "all", label: "Any status" },
    { value: "reviewed", label: "Reviewed" },
    { value: "completed", label: "Completed (no review)" },
    { value: "active", label: "In progress" },
    { value: "draft", label: "Draft" },
];

export default function HistoryPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    useDocumentTitle("History");

    // URL-backed state
    const page = parseInt(searchParams.get("page") || "1", 10);
    const sort = searchParams.get("sort") || "recent";
    const statusFilter = searchParams.get("status") || "all";
    const therapyFilter = searchParams.get("therapy_type") || "all";
    const minScore = parseInt(searchParams.get("min_score") || "0", 10);
    const maxScore = parseInt(searchParams.get("max_score") || "100", 10);
    // Phase 8 — "series" (default) or "sessions"
    const view = searchParams.get("view") === "sessions" ? "sessions" : "series";

    // In-flight slider value (committed to URL on commit)
    const [scoreLocal, setScoreLocal] = useState([minScore, maxScore]);

    const [data, setData] = useState(null); // { items, page, page_size, total, total_pages }
    const [therapyTypes, setTherapyTypes] = useState([]);
    const [loading, setLoading] = useState(true);

    // Load therapy types once for the filter pills
    useEffect(() => {
        api.get("/therapy-types").then((r) => setTherapyTypes(r.data)).catch(() => setTherapyTypes([]));
    }, []);

    // Sync local slider when URL changes externally
    useEffect(() => {
        setScoreLocal([minScore, maxScore]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("min_score"), searchParams.get("max_score")]);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                page_size: PAGE_SIZE,
                sort,
            };
            if (statusFilter !== "all") params.status = statusFilter;
            if (therapyFilter !== "all") params.therapy_type = therapyFilter;
            if (minScore > 0) params.min_score = minScore;
            if (maxScore < 100) params.max_score = maxScore;
            const res = await api.get("/sessions/mine", { params });
            setData(res.data);
        } catch (err) {
            toast.error("Could not load your sessions");
            setData({ items: [], total: 0, total_pages: 0, page: 1, page_size: PAGE_SIZE });
        } finally {
            setLoading(false);
        }
    }, [page, sort, statusFilter, therapyFilter, minScore, maxScore]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value == null || value === "all" || value === "" || value === false) {
            next.delete(key);
        } else {
            next.set(key, String(value));
        }
        // any filter change resets page to 1
        if (key !== "page") next.delete("page");
        setSearchParams(next, { replace: true });
    };

    const commitScoreRange = (range) => {
        const next = new URLSearchParams(searchParams);
        if (range[0] > 0) next.set("min_score", String(range[0]));
        else next.delete("min_score");
        if (range[1] < 100) next.set("max_score", String(range[1]));
        else next.delete("max_score");
        next.delete("page");
        setSearchParams(next, { replace: true });
    };

    const hasActiveFilters = useMemo(
        () => statusFilter !== "all" || therapyFilter !== "all" || minScore > 0 || maxScore < 100 || sort !== "recent",
        [statusFilter, therapyFilter, minScore, maxScore, sort]
    );

    const clearFilters = () => setSearchParams({}, { replace: true });

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in-up" data-testid="history-page">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="text-sm font-medium text-primary">Your practice</p>
                        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                            My practice sessions
                        </h1>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Every session you&apos;ve run, with scores and reviews.
                        </p>
                    </div>
                    <Button asChild className="bg-primary hover:bg-primary-hover" data-testid="history-new-session-button">
                        <Link to="/practice/setup">
                            New session
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Button>
                </div>

                {/* Sticky filter bar */}
                <Card
                    className="sticky top-16 z-10 border-border/80 bg-background/95 shadow-sm backdrop-blur"
                    data-testid="history-filter-bar"
                >
                    <CardContent className="grid gap-4 p-4 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
                            <Select value={statusFilter} onValueChange={(v) => updateParam("status", v)}>
                                <SelectTrigger data-testid="history-filter-status">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUS_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value} data-testid={`history-filter-status-${o.value}`}>
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Therapy type</Label>
                            <Select value={therapyFilter} onValueChange={(v) => updateParam("therapy_type", v)}>
                                <SelectTrigger data-testid="history-filter-therapy">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Any therapy</SelectItem>
                                    {therapyTypes.map((t) => (
                                        <SelectItem key={t.slug} value={t.slug} data-testid={`history-filter-therapy-${t.slug}`}>
                                            {t.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Score range</Label>
                                <span className="text-xs tabular-nums text-muted-foreground" data-testid="history-score-range-label">
                                    {scoreLocal[0]} – {scoreLocal[1]}
                                </span>
                            </div>
                            <Slider
                                value={scoreLocal}
                                onValueChange={setScoreLocal}
                                onValueCommit={commitScoreRange}
                                min={0}
                                max={100}
                                step={5}
                                data-testid="history-filter-score-slider"
                                className="py-2"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Sort</Label>
                            <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
                                <SelectTrigger data-testid="history-filter-sort">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SORT_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value} data-testid={`history-filter-sort-${o.value}`}>
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5 md:col-span-4">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">View</Label>
                            <div className="inline-flex rounded-lg border border-border bg-secondary/50 p-0.5" data-testid="history-view-toggle">
                                <button
                                    type="button"
                                    onClick={() => updateParam("view", "series")}
                                    className={cn(
                                        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition",
                                        view === "series"
                                            ? "bg-background text-foreground shadow-sm"
                                            : "text-muted-foreground hover:text-foreground"
                                    )}
                                    data-testid="history-view-series"
                                >
                                    <Layers className="h-3.5 w-3.5" />
                                    Grouped by series
                                </button>
                                <button
                                    type="button"
                                    onClick={() => updateParam("view", "sessions")}
                                    className={cn(
                                        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition",
                                        view === "sessions"
                                            ? "bg-background text-foreground shadow-sm"
                                            : "text-muted-foreground hover:text-foreground"
                                    )}
                                    data-testid="history-view-sessions"
                                >
                                    <GalleryVertical className="h-3.5 w-3.5" />
                                    All sessions
                                </button>
                            </div>
                        </div>
                        {hasActiveFilters && (
                            <div className="md:col-span-4">
                                <Button
                                    onClick={clearFilters}
                                    variant="ghost"
                                    size="sm"
                                    data-testid="history-filter-clear"
                                    className="h-8 text-xs"
                                >
                                    <RotateCcw className="h-3 w-3" />
                                    Clear filters
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Result count */}
                {data && (
                    <p className="text-sm text-muted-foreground" data-testid="history-result-count">
                        {data.total === 0
                            ? "No sessions match your filters."
                            : `${data.total} session${data.total === 1 ? "" : "s"} found`}
                    </p>
                )}

                {/* Grid */}
                {loading && !data ? (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, i) => <AvatarCardSkeleton key={i} />)}
                    </div>
                ) : data?.items.length === 0 ? (
                    <EmptyState
                        icon={History}
                        title={hasActiveFilters ? "No sessions match these filters" : "No sessions yet"}
                        description={
                            hasActiveFilters
                                ? "Try widening the score range or clearing some filters."
                                : "Your first session will show up here once you've finished one."
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="secondary" size="sm" onClick={clearFilters} data-testid="history-empty-clear">
                                    Clear filters
                                </Button>
                            ) : (
                                <Button asChild variant="secondary" size="sm">
                                    <Link to="/practice/setup">
                                        Start your first practice
                                        <ArrowRight className="h-3 w-3" />
                                    </Link>
                                </Button>
                            )
                        }
                    />
                ) : (
                    view === "series" ? (
                        <SeriesGrid items={data?.items || []} />
                    ) : (
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="history-grid">
                            {data?.items.map((s) => (
                                <SessionCard key={s.id} session={s} />
                            ))}
                        </div>
                    )
                )}

                {/* Pagination */}
                {data && data.total_pages > 1 && (
                    <Paginator
                        page={data.page}
                        totalPages={data.total_pages}
                        onChange={(p) => updateParam("page", p)}
                    />
                )}
            </div>
        </Layout>
    );
}

const Paginator = ({ page, totalPages, onChange }) => {
    // Compact: always show first/last; show neighbours of current; ellipsis around gaps.
    const pages = useMemo(() => {
        const set = new Set([1, totalPages, page - 1, page, page + 1]);
        return Array.from(set)
            .filter((p) => p >= 1 && p <= totalPages)
            .sort((a, b) => a - b);
    }, [page, totalPages]);

    return (
        <Pagination className="pt-4" data-testid="history-pagination">
            <PaginationContent>
                <PaginationItem>
                    <PaginationPrevious
                        onClick={(e) => {
                            e.preventDefault();
                            if (page > 1) onChange(page - 1);
                        }}
                        href="#"
                        aria-disabled={page <= 1}
                        className={page <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        data-testid="pagination-prev"
                    />
                </PaginationItem>
                {pages.map((p, i) => {
                    const showEllipsis = i > 0 && p - pages[i - 1] > 1;
                    return (
                        <React.Fragment key={p}>
                            {showEllipsis && (
                                <PaginationItem>
                                    <PaginationEllipsis />
                                </PaginationItem>
                            )}
                            <PaginationItem>
                                <PaginationLink
                                    isActive={p === page}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onChange(p);
                                    }}
                                    href="#"
                                    className="cursor-pointer"
                                    data-testid={`pagination-page-${p}`}
                                >
                                    {p}
                                </PaginationLink>
                            </PaginationItem>
                        </React.Fragment>
                    );
                })}
                <PaginationItem>
                    <PaginationNext
                        onClick={(e) => {
                            e.preventDefault();
                            if (page < totalPages) onChange(page + 1);
                        }}
                        href="#"
                        aria-disabled={page >= totalPages}
                        className={page >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        data-testid="pagination-next"
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    );
};


// ---------------------------------------------------------------------------
// Phase 8 — Series grouping
// ---------------------------------------------------------------------------
const SCORE_TONE = (score) => {
    if (score == null) return "border-border bg-secondary text-muted-foreground";
    if (score >= 75) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (score >= 55) return "border-amber-200 bg-amber-50 text-amber-800";
    return "border-rose-200 bg-rose-50 text-rose-700";
};

const SeriesGrid = ({ items }) => {
    // Group by series_id, preserving the order of first appearance from the
    // (already-sorted) flat list of sessions.
    const groups = useMemo(() => {
        const map = new Map();
        for (const s of items) {
            const sid = s.series_id || s.id;
            if (!map.has(sid)) map.set(sid, []);
            map.get(sid).push(s);
        }
        // Inside each group, sort by session_number_in_series ascending.
        for (const list of map.values()) {
            list.sort((a, b) => (a.session_number_in_series || 1) - (b.session_number_in_series || 1));
        }
        return Array.from(map.entries()).map(([series_id, sessions]) => ({ series_id, sessions }));
    }, [items]);

    if (groups.length === 0) return null;

    return (
        <div className="space-y-4" data-testid="history-series-grid">
            {groups.map(({ series_id, sessions }) => (
                <SeriesCard key={series_id} seriesId={series_id} sessions={sessions} />
            ))}
        </div>
    );
};

const SeriesCard = ({ seriesId, sessions }) => {
    const navigate = useNavigate();
    const first = sessions[0];
    const last = sessions[sessions.length - 1];
    const canContinue =
        sessions.length < 8 && (last.status === "completed" || last.status === "reviewed");
    const isSingle = sessions.length === 1;

    return (
        <Card
            className="border-border/80 shadow-sm transition hover:border-primary/30 hover:shadow-md"
            data-testid={`history-series-card-${seriesId}`}
        >
            <CardContent className="space-y-4 p-5">
                <div className="flex items-start gap-4">
                    <div className={cn("shrink-0 overflow-hidden rounded-xl", first.is_couple ? "h-14 w-24" : "h-14 w-14")}>
                        <AvatarPortrait
                            name={first.avatar_name}
                            imageUrl={first.avatar_image_url}
                            aspect={first.is_couple ? "wide" : "square"}
                            rounded="rounded-xl"
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h3 className="truncate font-semibold text-foreground" data-testid={`history-series-name-${seriesId}`}>
                                {first.avatar_name}
                            </h3>
                            {!isSingle && (
                                <span
                                    className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                                    data-testid={`history-series-count-${seriesId}`}
                                >
                                    {sessions.length}-session series
                                </span>
                            )}
                        </div>
                        <p className="truncate text-sm text-muted-foreground">{first.case_title}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            {first.therapy_type_name} · started {new Date(first.created_at).toLocaleDateString()}
                        </p>
                    </div>
                    {canContinue && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0"
                            onClick={() => navigate(`/practice/setup?continue_from=${last.id}`)}
                            data-testid={`history-series-continue-${seriesId}`}
                        >
                            Continue
                            <ArrowRight className="h-3 w-3" />
                        </Button>
                    )}
                </div>

                <div className="flex flex-wrap gap-2" data-testid={`history-series-pills-${seriesId}`}>
                    {sessions.map((s) => (
                        <Link
                            key={s.id}
                            to={`/sessions/${s.id}/review`}
                            className={cn(
                                "group flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition hover:shadow-sm",
                                SCORE_TONE(s.overall_score)
                            )}
                            data-testid={`history-series-pill-${s.id}`}
                            title={`Session ${s.session_number_in_series} of 8 — ${s.status}`}
                        >
                            <span className="opacity-70">#{s.session_number_in_series}</span>
                            <span className="font-semibold tabular-nums">
                                {s.overall_score != null ? s.overall_score : "—"}
                            </span>
                            <span className="text-[10px] uppercase tracking-wider opacity-60">
                                {s.status === "reviewed" ? "reviewed" : s.status}
                            </span>
                        </Link>
                    ))}
                    {sessions.length < 8 && (
                        <span className="flex items-center gap-1 rounded-lg border border-dashed border-border px-3 py-1.5 text-xs text-muted-foreground">
                            <span>+ {8 - sessions.length} more possible</span>
                        </span>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};
